import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDomainIntelligenceFile } from './domainIntelligenceApi'

// Customer-language labels from the workspace's own metadata.json
// (terminology.labels + terminology.hierarchy_plural_labels) — the same section
// the DOCX and Portal renderers consume. The operator UI reads it too so that a
// retail customer sees "Stores", a real-estate customer sees "Agents", and a
// hospitality customer sees "Properties" without any hardcoded business noun.
// Every lookup takes a neutral fallback for workspaces with no terminology.
export const useTerminology = (workspaceID) => {
    const { data } = useQuery({
        queryKey: ['domainIntelligenceFile', workspaceID, 'metadata.json'],
        queryFn: () => getDomainIntelligenceFile(workspaceID, 'metadata.json'),
        enabled: Boolean(workspaceID),
        staleTime: 5 * 60 * 1000,
    })

    return useMemo(() => {
        const terminology = data?.content?.terminology || {}
        const labels = terminology.labels || {}
        const plurals = terminology.hierarchy_plural_labels || {}

        return {
            /** labels lookup, e.g. label('flagged', 'Flagged') */
            label: (key, fallback) =>
                typeof labels[key] === 'string' && labels[key].trim() !== ''
                    ? labels[key]
                    : fallback,
            /** plural for a hierarchy level name, e.g. plural('Store Number', 'Entities') */
            plural: (levelName, fallback) =>
                typeof plurals[levelName] === 'string' && plurals[levelName].trim() !== ''
                    ? plurals[levelName]
                    : fallback,
            hasTerminology: Object.keys(labels).length > 0,
        }
    }, [data])
}
